import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { GetLocationListResponseType } from 'features/Location/@types/location'
import { GetActiveProductCategoriesType } from 'features/Product/@types/Product'
import { GetAreaDetailResponseType, GetAreaListResponseType } from 'features/Rack/services/area'
import { GetRackStorageResponseType } from 'features/Rack/@types/rack'

const SLICE_NAME = 'MASTER_AREA/list'

export type LocationListType = GetLocationListResponseType['content']

export type CategoryItemType = Pick<
  GetActiveProductCategoriesType['categories'][number],
  'id' | 'name'
>

export type RackStorageListType = GetRackStorageResponseType['data']['content']

export type AreaListSliceType = {
  isLoading: boolean
  isLocationLoading: boolean
  constants: {
    locations: GetLocationListResponseType['content']
  }
  filter: {
    selectedLocation: {
      location_id: number
      location_name: string
    }
    pageIndex: number
    pageSize: number
  }
  data: GetAreaListResponseType
  pagination: {
    pageSize: number
    pageIndex: number
    numberOfElements: number
  }
  modalArea: {
    type: 'add' | 'edit' | 'detail'
    isOpen: boolean
    selectedId: number
    areaList: GetAreaListResponseType
    isLoadingWimsCategory: boolean
    wimsCategoryList: CategoryItemType[]
    categoryList: CategoryItemType[]
    rackStorageList: RackStorageListType
    locationList: LocationListType
    isSubmittingData: boolean
  }
  areaDetail: Nullable<GetAreaDetailResponseType>
}

const initialState: AreaListSliceType = {
  isLoading: false,
  isLocationLoading: false,
  constants: {
    locations: [],
  },
  filter: {
    selectedLocation: {
      location_id: 0,
      location_name: '',
    },
    pageIndex: 1,
    pageSize: 20,
  },
  data: [],
  pagination: {
    pageSize: 20,
    pageIndex: 0,
    numberOfElements: 0,
  },
  modalArea: {
    type: 'add',
    isOpen: false,
    selectedId: 0,
    areaList: [],
    isLoadingWimsCategory: false,
    wimsCategoryList: [],
    categoryList: [],
    rackStorageList: [],
    locationList: [],
    isSubmittingData: false,
  },
  areaDetail: null,
}

const areaSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset: () => initialState,
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setFilter: (state, action: PayloadAction<Partial<AreaListSliceType['filter']>>) => {
      state.filter = {
        ...state.filter,
        ...action.payload,
      }
    },
    setModalArea: (state, action: PayloadAction<Partial<AreaListSliceType['modalArea']>>) => {
      state.modalArea = {
        ...state.modalArea,
        ...action.payload,
      }
    },
    resetModalState: (state) => {
      state.modalArea = initialState.modalArea
    },
    setLocationList: (state, action: PayloadAction<GetLocationListResponseType['content']>) => {
      state.isLocationLoading = false
      state.constants.locations = action.payload
    },
    setAreaList: (state, action) => {
      const { data, pagination } = action.payload

      state.data = data

      if (pagination) {
        state.pagination = {
          pageSize: pagination.page_size as number,
          pageIndex: pagination.page_index as number,
          numberOfElements: pagination.number_of_elements as number,
        }
      }
    },
    setAreaDetail: (state, action) => {
      state.isLoading = false
      state.areaDetail = action.payload
    },
  },
})

export default areaSlice.reducer
export const {
  reset,
  setIsLoading,
  setFilter,
  setModalArea,
  setLocationList,
  setAreaList,
  setAreaDetail,
  resetModalState,
} = areaSlice.actions
